import { UserOutlined } from '@ant-design/icons';
import { App, Button, Form, Input, Modal, Table, Tag } from 'antd';
import { SITE } from 'config/config';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { claimReward, historyPredictions } from 'service/base';
import { AUTH_CHANGED_EVENT, getStoredAccessToken } from 'utils/authSession';
import 'styles/global.css';

function readSavedAccount() {
  return (
    localStorage.getItem('userAccount') ||
    localStorage.getItem('hi88_login_username') ||
    localStorage.getItem('saved_account') ||
    ''
  );
}

/** Giống header / Home: chỉ khi có token mới lấy username đã đăng nhập. */
function getLoggedInUsernameForForm() {
  if (!getStoredAccessToken()) return '';
  return String(localStorage.getItem('hi88_login_username') || 'Thành viên').trim();
}

const HistoryPredictions = ({
  isModalVote,
  setIsModalVote,
  IP,
  FP,
  embedded = false,
  title = 'DANH SÁCH LỊCH SỬ DỰ ĐOÁN'
}) => {
  const { notification } = App.useApp();
  const [formHistory] = Form.useForm();
  const [account, setAccount] = useState('');
  const [dataPrediction, setDataPrediction] = useState([]);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);

  const defaultPagination = { current: 1, pageSize: 15 };
  const [pagination, setPagination] = useState({
    current: defaultPagination.current,
    pageSize: defaultPagination.pageSize,
    total: 0,
    size: 'default',
    onChange: (page, pageSize) => {
      setPagination((prev) => ({ ...prev, current: page, pageSize }));
    }
  });

  const fetchHistoryPredictions = useCallback(
    async (accountValue) => {
      const normalizedAccount = String(accountValue ?? '').trim();
      if (!normalizedAccount) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng nhập tên đăng nhập!'
        });
        return;
      }

      setIsLoadingSubmit(true);
      try {
        const res = await historyPredictions({
          account: normalizedAccount,
          site: SITE,
          ip: IP?.ip ?? IP,
          fp: FP?.fp ?? FP
        });

        const payload = res?.data || [];
        setDataPrediction(payload);
        setPagination((prev) => ({
          ...prev,
          total: payload?.totalRecords ?? payload?.responseData?.length ?? 0,
          current: 1
        }));

        if (!payload?.responseData?.[0]) {
          notification.warning({
            message: 'Không có lịch sử',
            description: 'Không tìm thấy lịch sử bình chọn, Quý khách vui lòng kiểm tra lại!'
          });
        }
      } finally {
        setIsLoadingSubmit(false);
      }
    },
    [IP, FP, notification]
  );

  useEffect(() => {
    if (!embedded && !isModalVote) return;

    const loginAccount = getLoggedInUsernameForForm();
    if (loginAccount) {
      setAccount(loginAccount);
      formHistory.setFieldsValue({ account: loginAccount });
      void fetchHistoryPredictions(loginAccount);
      return;
    }

    const saved = readSavedAccount();
    if (saved) {
      setAccount(saved);
      formHistory.setFieldsValue({ account: saved });
    }
  }, [embedded, isModalVote, IP, FP, fetchHistoryPredictions, formHistory]);

  useEffect(() => {
    if (!embedded && !isModalVote) return;

    const onAuth = () => {
      const acc = getLoggedInUsernameForForm();
      if (acc) {
        setAccount(acc);
        formHistory.setFieldsValue({ account: acc });
        void fetchHistoryPredictions(acc);
      } else {
        setAccount('');
        formHistory.setFieldsValue({ account: '' });
        setDataPrediction([]);
        setPagination((prev) => ({ ...prev, total: 0, current: 1 }));
      }
    };

    window.addEventListener(AUTH_CHANGED_EVENT, onAuth);
    return () => window.removeEventListener(AUTH_CHANGED_EVENT, onAuth);
  }, [embedded, isModalVote, fetchHistoryPredictions, formHistory]);

  const handleCancelVote = () => {
    if (!embedded) setIsModalVote(false);
  };

  const onFinishHistoryForm = async (data) => {
    const normalizedAccount = String(data?.account ?? '').trim();
    setAccount(normalizedAccount);
    await fetchHistoryPredictions(normalizedAccount);
  };

  const onFinishHistoryFailed = () => {
    notification.warning({
      message: 'Cảnh báo',
      description: 'Vui lòng kiểm tra lại tài khoản !'
    });
  };

  const handleClaimReward = async (item) => {
    if (item.status_register !== 'apply') {
      return;
    }
    try {
      const accountValue = String(formHistory.getFieldValue('account') ?? item.account ?? account)
        .trim()
        .toLowerCase();
      const response = await claimReward({
        account: accountValue,
        site: SITE,
        matchId: item.matchId
      });
      if (response?.code === 0) {
        notification.success({ message: 'Thành công', description: 'Nhận thưởng thành công!' });
        await fetchHistoryPredictions(accountValue);
      }
    } catch (error) {
      notification.error({
        message: 'Nhận thưởng thất bại!',
        description: error.message
      });
    }
  };

  const columns = [
    {
      title: 'Trận đấu',
      width: '15%',
      render: (_, record) => record.homeName + ' - ' + record.awayName
    },
    {
      title: 'Ngày dự đoán',
      dataIndex: 'createdAt',
      width: '10%',
      render: (_, record) => dayjs(record.createdAt).format('DD/MM/YYYY HH:mm')
    },
    {
      title: 'Bình chọn',
      width: '6%',
      render: (_, record) => (
        <Tag className="text-center" color="#000000" key={record._id}>
          {record.homeScore}-{record.awayScore}
        </Tag>
      )
    },
    {
      title: 'Dự đoán giống bạn',
      width: '6%',
      render: (_, record) => (
        <Tag className="text-center" color="#000000" key={record._id}>
          {record.sameScore}
        </Tag>
      )
    },
    {
      title: 'Cấp VIP',
      width: '6%',
      render: (_, record) => (
        <Tag className="text-center" color="#000000" key={record._id}>
          {record.vipLevel ?? '-'}
        </Tag>
      )
    },
    {
      title: 'Kết quả',
      width: '6%',
      render: (_, record) => {
        if (record.match_result) {
          return (
            <Tag className="text-center" color="#00890a" key={record._id}>
              {record.match_result}
            </Tag>
          );
        }
        return null;
      }
    },
    {
      title: 'Điểm thưởng',
      width: '8%',
      render: (_, record) => (record.point > 0 ? record.point : '-')
    },
    {
      title: 'Nhận thưởng',
      width: '16%',
      render: (_, item) => (
        <div className="flex justify-start">
          <button
            type="button"
            onClick={() => handleClaimReward(item)}
            disabled={item.status_register !== 'apply'}
            className={`rounded-md border-none px-1 py-2 text-xs font-semibold text-white md:rounded-lg md:px-3 md:text-sm ${item.status_register === 'apply' ? 'cursor-pointer bg-[#F49A25]' : 'cursor-not-allowed bg-[#929292]'}`}
          >
            {item.status_register === 'accept' ? 'Đã nhận thưởng' : 'Nhận thưởng'}
          </button>
        </div>
      )
    }
  ];

  const content = (
    <div
      className={
        embedded ? 'content-km content-history px-0 md:px-1' : 'content-km content-history'
      }
    >
      <Form
        name="formHistoryMatch"
        onFinish={onFinishHistoryForm}
        onFinishFailed={onFinishHistoryFailed}
        className="relative"
        autoComplete="off"
        form={formHistory}
      >
        <div className="hidden relative flex w-full justify-between gap-2 py-1 md:justify-center md:py-4">
          <Form.Item
            name="account"
            className="mb-0 min-w-[30%] w-full max-w-[600px] md:min-h-[38px]"
            rules={[
              {
                required: true,
                message: 'Vui lòng điền tên đăng nhập!'
              }
            ]}
          >
            <Input
              className="min-h-[38px] w-full"
              placeholder="Nhập tên đăng nhập ..."
              maxLength={30}
              prefix={<UserOutlined />}
            />
          </Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={isLoadingSubmit}
            className="bg-score bg-check-account absolute right-3 top-[50%] z-30 min-h-[32px] -translate-y-1/2 font-medium text-white"
          >
            Xác Nhận
          </Button>
        </div>
      </Form>
      <div className="list-history">
        <Table
          size="small"
          rowKey={(r) => r._id ?? `${r.matchId}-${r.account}`}
          columns={columns}
          dataSource={dataPrediction?.responseData || []}
          loading={isLoadingSubmit}
          pagination={pagination}
          scroll={{ x: 'max-content' }}
        />
      </div>
    </div>
  );

  if (embedded) return content;

  return (
    <Modal
      className="modal-jun88k36 modal-history"
      title={title}
      open={isModalVote}
      footer={null}
      onCancel={handleCancelVote}
    >
      {content}
    </Modal>
  );
};

export default HistoryPredictions;
